import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { AlertTriangle, FileWarning, TrendingUp, Stethoscope } from 'lucide-react';

const copy = {
  en: {
    title: 'Why Now',
    items: [
      { title: 'Operational pressure in veterinary care', text: 'Veterinary teams face growing documentation load, staff pressure, and inconsistent workflows across clinics.' },
      { title: 'Fragmented clinical workflows', text: 'Many clinics still rely on manual processes for calls, history collection, consultation notes, and follow-up documentation.' },
      { title: 'AI adoption is accelerating', text: 'Veterinary practices are increasingly adopting AI tools that reduce administrative burden and help clinicians focus on patient care.' },
      { title: 'Built from real clinical practice', text: 'Dr.Vet was developed and refined in active veterinary clinics in Ukraine, giving the platform practical insight into real-world workflows and challenges.' },
    ],
  },
  uk: {
    title: 'Чому саме зараз',
    items: [
      { title: 'Операційний тиск у ветеринарній практиці', text: 'Ветеринарні команди стикаються зі зростаючим навантаженням від документації, тиском на персонал та непослідовними процесами в клініках.' },
      { title: 'Фрагментовані клінічні процеси', text: 'Багато клінік досі покладаються на ручні процеси для дзвінків, збору анамнезу, нотаток консультацій та подальшої документації.' },
      { title: 'Впровадження AI пришвидшується', text: 'Ветеринарні практики дедалі активніше використовують AI-інструменти, що зменшують адміністративне навантаження та дозволяють лікарям зосередитися на пацієнтах.' },
      { title: 'Створено на основі реальної клінічної практики', text: 'Dr.Vet розроблено та відточено в діючих ветеринарних клініках в Україні, що дає платформі практичне розуміння реальних процесів і викликів.' },
    ],
  },
  ja: {
    title: 'なぜ今か',
    items: [
      { title: '獣医診療における運用上の負荷', text: '獣医チームは、増え続ける文書作成、人員への負担、クリニック間で一貫しないワークフローに直面しています。' },
      { title: '分断された臨床ワークフロー', text: '多くのクリニックでは、電話対応、病歴収集、診察記録、フォローアップ文書を依然として手作業に頼っています。' },
      { title: 'AI導入が加速しています', text: '獣医診療では、管理業務の負担を軽減し、臨床医が診療に集中できるようにするAIツールの採用が広がっています。' },
      { title: '実際の臨床現場から生まれた', text: 'Dr.Vetはウクライナの稼働中の獣医クリニックで開発・改良され、実際のワークフローと課題に対する実践的な洞察を備えています。' },
    ],
  },
};

const icons = [AlertTriangle, FileWarning, TrendingUp, Stethoscope];

export default function WhyNowSection() {
  const { lang } = useLanguage();
  const c = copy[lang];

  return (
    <section className="section-padding section-alt">
      <div className="container-max">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl md:text-4xl font-bold text-foreground text-center mb-10"
        >
          {c.title}
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {c.items.map((item, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card-elevated p-5 flex flex-col gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon size={20} className="text-primary" />
                </div>
                <h3 className="text-base font-bold text-foreground leading-snug">{item.title}</h3>
                <p className="text-sm text-foreground/80 leading-relaxed">{item.text}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
